import { ChangeDetectorRef, Component, ComponentFactoryResolver, ComponentRef, HostListener, OnInit, ViewChild, inject, DestroyRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { translate } from '@jsverse/transloco';
import { Observable, Subscription, combineLatest, finalize, map, mergeMap, of } from 'rxjs';
import { BreadcrumbItem } from 'src/app/models/breadcrumb/breadcrumb-item';
import { RouterSegments } from 'src/app/models/router-segments';
import { Solution } from 'src/app/models/service/solution';
import { SolutionVersion } from 'src/app/models/service/solution-version';
import { SolutionsService } from 'src/app/services/api/solutions.service';
import { AuthService } from 'src/app/services/auth/auth.service';
import { ErrorPageService } from 'src/app/services/util/error-page.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BlueprintsService } from 'src/app/services/api/blueprints.service';
import { Blueprint, InstanceTabs } from 'src/app/models/blueprints/blueprint';
import { InstancesService } from 'src/app/services/api/instances.service';
import { Instance } from 'src/app/models/instance/instance';
import { Configuration } from 'src/app/models/configurations/configuration';
import { DeploymentClass } from 'src/app/models/instance-class';
import { UrlService } from 'src/app/services/util/url.service';
import { SidenavService } from 'src/app/services/util/sidenav.service';
import { ConfigurationsService } from 'src/app/services/api/configurations.service';
import {
  ConfigurationApplicationStatus, ConfigurationPackageStatus,
  ConfigurationState
} from 'src/app/models/configurations/configuration-state';
import { Sort, SortDirection } from 'src/app/models/sort';
import { Constants } from 'src/app/models/constants';
import { KeyValueMap } from 'src/app/models/key-value-map';
import { CRVTService } from 'src/app/services/api/crvt.service';
import { PaginatorComponent } from 'src/app/components/paginator/paginator.component';
import { isDate } from 'src/app/services/util/utils';
import { DataZone } from 'src/app/models/data-zone/data-zone';
import { MatDialog } from '@angular/material/dialog';
import { NotificationsService } from 'src/app/services/api/notifications.service';
import { SolutionVersionsService } from 'src/app/services/util/solution-versions.service';
import { ErrorDialogComponent } from 'src/app/components/error-dialog/error-dialog.component';
import { DeployStatus } from 'src/app/models/deploy/deploy-status';
import { ClipboardService } from 'ngx-clipboard';
import { ToastrService } from 'ngx-toastr';
import { CustomToastrService } from 'src/app/services/util/toastr.service';
import { ToastrErrorService } from 'src/app/services/util/toastr-error.service';
import { MatTableDataSource } from '@angular/material/table';
import { ColumnFilterComponent } from '../../instances-mgmt/column-filter/column-filter.component';
import { GenericErrorComponent } from 'src/app/components/generic-error/generic-error.component';
import { InstanceDetailsAddRouteComponent } from 'src/app/components/instance-details/instance-details-add-route/instance-details-add-route.component';
import { SweetAlertService } from 'src/app/services/util/sweet-alert.service';
import { DatePipe } from '@angular/common'
import { InstanceDatePickerComponent } from 'src/app/components/instance-details/instance-date-picker/instance-date-picker.component';
import { SolutionVersionsInfo } from 'src/app/models/service/solution-versions-info';
import { EndpointType } from 'src/app/models/endpoint-type';
import { DateTimeFormatService } from 'src/app/services/util/date-time-format.service';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { CustomTooltipComponent } from '../../custom-tooltip/custom-tooltip.component';
import { PermissionsService } from 'src/app/services/util/permissions.service';
import { Permission } from 'src/app/models/principal/permission';
import { Permissions, Datazone, Attributes } from 'src/app/models/principal/permission';
import { PermissionKeys } from 'src/app/models/principal/permission-keys';
import { AgentStatus } from 'src/app/models/agents/agent-status';
import { Title } from '@angular/platform-browser';
import { ConfirmationDialogComponent } from 'src/app/components/confirmation-dialog/confirmation-dialog.component';
import { ActivityService } from 'src/app/services/api/activity.service';
type ComparatorFunc = (first: any, second: any) => number;
const REQUIRED_PERMISSIONS = [
  {
    key: PermissionKeys.INSTANCE_AGENT_MANAGE_DATAZONE_NONPII,
    action: Permissions.INSTANCE_AGENT_MANAGE,
    attribute: Attributes.DATAZONE,
    attributeValue: Datazone.NONPII
  },
  {
    key: PermissionKeys.INSTANCE_AGENT_MANAGE_DATAZONE_PII,
    action: Permissions.INSTANCE_AGENT_MANAGE,
    attribute: Attributes.DATAZONE,
    attributeValue: Datazone.PII
  },
  {
    key: PermissionKeys.INSTANCE_AGENT_MANAGE_DATAZONE_LIVE,
    action: Permissions.INSTANCE_AGENT_MANAGE,
    attribute: Attributes.DATAZONE,
    attributeValue: Datazone.LIVE
  }
];

@Component({
    selector: 'app-tenant-instance-details',
    templateUrl: './tenant-instance-details.component.html',
    styleUrls: ['./tenant-instance-details.component.scss'],
    animations: [
        trigger('detailExpand', [
            state('collapsed', style({ height: '0px', minHeight: '0' })),
            state('expanded', style({ height: '*' })),
            transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
        ]),
        trigger('rotate', [
            transition('void=>*', [style({ transform: 'rotate(0)' })]),
            transition('* => *', [
                style({ transform: 'rotate(0)' }),
                animate('650ms ease-in-out', style({ transform: 'rotate(360deg)' })),
            ])
        ])
    ],
    standalone: false
})
export class TenantInstanceDetailsComponent  implements OnInit {
  private destroyRef = inject(DestroyRef);
  @ViewChild(PaginatorComponent, { static: true }) paginator: PaginatorComponent;
  Permission = Permission;
  PermissionKeys = PermissionKeys;
  permissionsMap: { [key: string]: boolean };
  RouterSegments = RouterSegments;
  InstanceTabs = InstanceTabs;
  breadcrumbItems: BreadcrumbItem[];
  tenant: string;
  service: Solution;
  serviceVersions: SolutionVersion[];
  blueprint: Blueprint;
  instance: Instance;
  isLoading = true;
  isOverview = true;
  isEndpoints = false;
  configurations: Configuration[];
  buildNumber: string;
  releaseVersion: any;
  isAMR: boolean;
  isReleasePack = false;
  deploymentLog: any;
  updatedLog: any;
  activeTab: number = 0;
  fromCU = false;

  configurationStates: ConfigurationState[];
  configuration: Configuration[];
  pageConfigurationStates: ConfigurationState[];
  filteredConfigurationStates: ConfigurationState[];

  DataZone = DataZone;
  Constants = Constants;
  ConfigurationApplicationStatus = ConfigurationApplicationStatus;
  ConfigurationPackageStatus = ConfigurationPackageStatus;

  configurationsMap: KeyValueMap<string, Configuration>;
  searchTerm: string;

  SortDirection = SortDirection;
  sort: Sort;

  configStateVerify = false;
  allInstances: Instance[];
  skipTest: boolean;
  loadDataSubscription: Subscription;
  instanceBlueprint: Instance[];
  deployedDate = new Date();
  currentDate = new Date();
  availableConfigurations: Configuration[];
  applyConfigInProgress = false;
  appliedConfigurationState: ConfigurationState;
  selectedTab = 0;
  isVerifying = false;
  getBlueprints: any;
  filterOptions: any = [
    {
      label: 'All',
      value: ''
    },
    {
      label: 'Success',
      value: ['success']
    },
    {
      label: 'Failed',
      value: ['test_failed', 'configure_failed']
    }
  ];

  selectedFilter: any = {
    label: 'All',
    value: ''
  };

  allConfigurationStates: ConfigurationState[] = [];
  istanceLoaded = false;
  configurationLoaded = false;
  copy = false;
  instanceId: string;
  data: any;
  changeTaskId: string;
  tenantId: string;
  solution: any;
  solutionVersion: any;
  logLevel: any;
  currentEnvironment: any;
  direction: any;
  status: any;
  dropdownsList: any;
  clearData: boolean;
  sftpPaginator = false;
  logPaginator = false;
  showSpinner: boolean;
  isFailed: boolean;
  isLogFailed: boolean;
  logData: any;
  deploymentLogData: any;
  updatedLogData: any;
  routeData: any;
  sftp: boolean;
  displayedColumns: string[] = ['routeId', 'routeName', 'direction', 'status', 'menu'];
  logColumns: string[] = ['menu', 'action', 'startTime', 'finishTime', 'duration', 'success', 'failure', 'status', 'arrow'];
  innerLogColumns: string[] = ['componentName', 'version', 'startTime', 'endTime', 'duration', 'status', 'arrow'];
  pageIndex = 0;
  sortInfo: any;
  filteredColumns: any;
  dataSource: any;
  dataSourceLog: any;
  dataSourceUpdateLog: any;
  dataSourceInnerLog: any;
  dataSourceUpdateInnerLog: any;
  paginationSize = 10;
  showPagination = true;
  addingStatus: boolean;
  DeployStatus = DeployStatus;
  AgentStatus = AgentStatus;
  filterStartDate: any;
  filterEndDate: any;
  filterStartDateUpdateTab: any;
  filterEndDateUpdateTab: any;
  filteredDeploymentLog: any;
  filteredUpdateLog: any;
  expandedElement: any;
  isLive: boolean;
  serviceVersionsInfo: SolutionVersionsInfo;
  solutionVersions: any[];
  latestReleaseVersion: any;
  viewReleaseNotes: string;
  devDeploymentClass = DeploymentClass.D;
  instanceDetailsError: any;
  configTypeEndpoints: any;
  accessTypeEndpoints: any;
  exploreTypeEndpoints: any;
  otherTypeEndpoints: any;
  isEndpointAvailable = false;

  actionOptions = [];
  activityDropdownsList: any;
  selectedActions = [];
  previousSelectedActions = [];
  addColor: any;
  count: any;
  clientName: any;

  readonly INSTANCE_DEPLOYING_STATES = [DeployStatus.DEPLOYING, DeployStatus.TERMINATED,
    DeployStatus.CONFIGURING, DeployStatus.UPDATING, DeployStatus.OPERATION_INPROGRESS,
    DeployStatus.FAILOVER_APPLYING, DeployStatus.FAILBACK_APPLYING,
    DeployStatus.PRE_FAILBACK_APPLYING, DeployStatus.POST_FAILBACK_APPLYING, DeployStatus.TERMINATING, DeployStatus.TESTING, DeployStatus.INPROGRESS,];

    readonly INSTANCE_DEPLOYING_STATES_COUNT = [DeployStatus.DEPLOYING, DeployStatus.TERMINATED,
      DeployStatus.CONFIGURING, DeployStatus.UPDATING,
      DeployStatus.FAILOVER_APPLYING, DeployStatus.FAILBACK_APPLYING,
      DeployStatus.PRE_FAILBACK_APPLYING, DeployStatus.POST_FAILBACK_APPLYING, DeployStatus.TERMINATING, DeployStatus.TESTING,];

  readonly INSTANCE_DEPLOYED_STATES = [DeployStatus.DEPLOYED, DeployStatus.CONFIGURING,
  DeployStatus.CONFIGURATION_FAILED, DeployStatus.CONFIGURED, DeployStatus.UPDATING, DeployStatus.UPDATED, DeployStatus.TESTED,
  DeployStatus.UPDATE_FAILED, DeployStatus.TEST_FAILED];


  loaderStatus = [
    DeployStatus.DEPLOYING,
    DeployStatus.TERMINATING,
    DeployStatus.CONFIGURING,
    DeployStatus.UPDATING,
    DeployStatus.TESTING,
    DeployStatus.OPERATION_INPROGRESS,
    DeployStatus.STARTING_AGENT,
    DeployStatus.STOPPING_AGENT,
    DeployStatus.STARTING,
    DeployStatus.STOPPING
  ];

  statusIcons = {
    deployed: 'done',
    configured: 'check_circle',
    initialized: 'check_circle',
    updated: 'check_circle',
    tested: 'check_circle',
    failed: 'cancel_filled',
    configuration_failed: 'cancel_filled',
    terminated: 'error_outline',
    termination_failed: 'cancel_filled',
    test_failed: 'cancel_filled',
    update_failed: 'cancel_filled',
    stopped: 'error_outline',
    start_failed: 'cancel_filled',
    stop_failed: 'cancel_filled',
    initialize_failed: 'cancel_filled',
    deploying: 'change_circle',
    terminating: 'change_circle',
    configuring: 'change_circle',
    updating: 'change_circle',
    initializing: 'change_circle',
    operation_inprogress: 'change_circle',
    inprogress: 'change_circle',
    stopping: 'change_circle',
    stopping_agent: 'change_circle',
    starting: 'change_circle',
    starting_agent: 'change_circle',
    testing: 'change_circle',
    fallback_inprogress: 'change_circle',
    failover_inprogress: 'change_circle'
  };

  countData: any = {};
  intervalElement: any;

  readonly AGENT_STATES = [AgentStatus.RUNNING, AgentStatus.STOPPED]
  agentCreationProgress: any;
  releasePackage: any;
  solutionId: any;
  blueprintId: any;
  switchToNewViewForAzure: boolean;

  constructor(public authService: AuthService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private solutionsService: SolutionsService,
    private errorPageService: ErrorPageService,
    private blueprintsService: BlueprintsService,
    private instanceService: InstancesService,
    private urlService: UrlService,
    private sidenavService: SidenavService,
    private configService: ConfigurationsService,
    private crvtService: CRVTService,
    private changeDetectorRef: ChangeDetectorRef,
    private dialog: MatDialog,
    private sweetAlertService: SweetAlertService,
    private notificationsService: NotificationsService,
    private solutionVersionsService: SolutionVersionsService,
    private clipboardApi: ClipboardService,
    private toastrService: CustomToastrService,
    private toastrErrorService: ToastrErrorService,
    public datePipe: DatePipe,
    public dateTimeFormatService: DateTimeFormatService,
    private permissionsService: PermissionsService,
    private titleService: Title,
    private componentFactoryResolver: ComponentFactoryResolver,
    private activityService: ActivityService) {
    
  }

  ngOnInit(): void {
    this.clientName = this.router.url.split('.')[0].split('/')[3];
    this.activatedRoute.queryParams.subscribe(params => {
      this.fromCU = params['fromCU'] === 'true';
      this.sidenavService.tenantSelectedMenu = this.fromCU
        ? 'TENANT_LAYOUT.MENU.CONTINUOUS_UPDATE'
        : 'TENANT_LAYOUT.MENU.SERVICE_DEFINITION';
    });
    this.solutionId = this.activatedRoute.snapshot.params.solutionId;
    this.blueprintId = this.activatedRoute.snapshot.params.blueprintId;
    this.permissionsMap = this.permissionsService.resolveAPIPermissions(REQUIRED_PERMISSIONS);
    this.currentEnvironment = (window.location.href.split('.')[0].split('/')[2] === 'dev' ?
      'https://dev.portal.saas.temenos.cloud/' : window.location.href.split('.')[0].split('/')[2] === 'preprod' ?
        'https://preprod.portal.saas.temenos.cloud/' : window.location.href.split('.')[0].split('/')[2] === 'utp' ?
          'https://utp.portal.saas.temenos.cloud/' : 'https://portal.bankingcloud.temenos.com/');
    const statusFilteredData = localStorage.getItem('filteredSftpStatus');
    const directionFilteredData = localStorage.getItem('filteredDirection');
    if (statusFilteredData !== undefined) {
      this.status = statusFilteredData;
    }
    if (directionFilteredData !== undefined) {
      this.direction = directionFilteredData;
    }
    const directionOptions = [{ value: 'inbound', selected: false },
    { value: 'outbound', selected: false }
    ];
    const statusOptions = [
      { value: 'adding', selected: false },
      { value: 'added', selected: false },
      { value: 'failed', selected: false },
      { value: 'removing', selected: false },
      { value: 'removed', selected: false }
    ];
    this.dropdownsList = [
      {
        title: 'Direction',
        placeholder: 'Select Direction',
        options: directionOptions
      },
      {
        title: 'Status',
        placeholder: 'Select Status',
        options: statusOptions
      }
    ];
    this.clearData = true;

    this.subscribeForNotifications();
    this.tenant = this.authService.principal.provider;
    this.breadcrumbItems = [];
    this.loadService();
    this.instanceId = this.activatedRoute.snapshot.params.instanceId;
    // this.getInstanceDetails();
    this.paginator?.page
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(pageEvent => {
        this.applyPaginator();
      });
    this.activityDropdownsList = [
      {
        title: 'Action',
      },
      {
        title: 'Date',
      }
    ];
    this.getInstanceActivityProgress(this.instanceId);
    this.intervalElement = setInterval(() => {
      this.getInstanceActivityProgress(this.instanceId);
    }, 60000);
    this.activityService.getInstanceActivityLogs(this.instanceId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(data => {
        this.showSpinner = false;
        this.isLogFailed = true;
        this.logData = data;

        this.logData?.forEach(item => {
          if (!this.actionOptions.find(option => option.value === item.action)) {
            const actionOption = {
              value: item.action,
              selected: false
            }
            this.actionOptions = [...this.actionOptions, actionOption];
          }
          this.activityDropdownsList[0].options = this.actionOptions;
        })
      },
        (error) => {
          this.showSpinner = false;
          this.isLogFailed = true;
        setTimeout(() => {
		      const title = translate('ERRORS.SERVICE_AVAILABILITY');
          const message = error?.message ? error?.message : (error?.error?.message ? error?.error?.message : (error?.errmsg ? error?.errmsg : translate('SERVICE.INTERNAL_SERVER_ERROR')));
          this.toastrService.errMessage(message, title);
        }, 500);
        });
    this.logData?.forEach(data => data.arrowClick = false);
    this.deploymentLogData = this.logData;
    this.dataSourceLog = this.deploymentLogData;
    const blueprintId = this.activatedRoute.snapshot.params.blueprintId;
    this.configService.getConfigurations(blueprintId)
    .pipe(takeUntilDestroyed(this.destroyRef))
    .subscribe(configurations => {
     this.configurations = configurations;
     this.getReleasePackage();
    })
  }

  getInstanceDetails(): void {
    this.instanceService.getInstanceDetails(this.instanceId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(data => {
        if (data) {
          this.data = data;
          this.instanceDetailsError = data;
          this.logLevel = data.parameters;
          this.titleService.setTitle(this.data.name);
          this.breadcrumbItems[3] = { label: this.data.name };
          const getPTLEnvironment = this.getBlueprints?.pathToLiveConfiguration?.find(ptl => ptl.instanceId === this.data.id);
          this.data.environment = getPTLEnvironment?.environment;
          this.initialLoad();
          if (this.data?.endpoints) {
            this.configTypeEndpoints = this.data?.endpoints.filter(endpoint => endpoint?.useType === EndpointType.CONFIGURATION);
            this.accessTypeEndpoints = this.data?.endpoints.filter(endpoint => endpoint?.useType === EndpointType.UI);
            this.exploreTypeEndpoints = this.data?.endpoints.filter(endpoint => endpoint?.useType === EndpointType.API);
            this.otherTypeEndpoints = this.data?.endpoints.filter(endpoint => endpoint?.useType === EndpointType.OTHER);
            if (this.configTypeEndpoints.length > 0
              || this.accessTypeEndpoints.length > 0
              || this.exploreTypeEndpoints.length > 0
              || this.otherTypeEndpoints.length > 0) {
              this.isEndpointAvailable = true;
            }
          }
          this.isLive = this.resolveLiveStatus(data);
          this.data.solution = data;
          // this.getInstanceDetailsById();
          if (this.data.solutionId) {
            this.getSolutionById(this.data.solutionId);
          }
        }
      },
        (error) => {
        setTimeout(() => {
		      const title = translate('ERRORS.SERVICE_AVAILABILITY');
          const message = error?.message ? error?.message : (error?.error?.message ? error?.error?.message : (error?.errmsg ? error?.errmsg : translate('SERVICE.INTERNAL_SERVER_ERROR')));
          this.toastrService.errMessage(message, title);
        }, 500);
        });
  }

  getInstanceDetailsById(): void {
    this.instanceService.getInstanceDetailsById(this.data?.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(data => {
        if (data) {
          this.data.details = data;
          if (this.solution && this.solutionVersions) {
            this.serviceVersionsInfo = this.solutionVersionsService.getSolutionVersionsInfo(this.solution, this.solutionVersions, data);
            const liveInstance = data.find(instance => instance.deploymentClass === DeploymentClass.A);
            if (liveInstance) {
              this.latestReleaseVersion = this.solutionVersions.filter(version =>
                version.id === liveInstance.solutionVersionId)[0]?.releaseVersion;
            } else {
              const instanceLength = data.length;
              const latestInstanceSolutionVersion = data[instanceLength - 1]?.solutionVersionId;
              this.latestReleaseVersion = this.solutionVersions.filter(version =>
                version.id === latestInstanceSolutionVersion)[0]?.releaseVersion;
            }
            let releaseVersionText;
            if (this.latestReleaseVersion) {
              releaseVersionText = this.latestReleaseVersion;
            } else {
              releaseVersionText = this.serviceVersionsInfo?.currentVersion?.releaseVersion;
            }
            this.isAMR = releaseVersionText && this.solutionVersionsService.releaseVersion(releaseVersionText) ? true : false;
          }
        }
      },
        (error) => {
        setTimeout(() => {
		      const title = translate('ERRORS.SERVICE_AVAILABILITY');
          const message = error?.message ? error?.message : (error?.error?.message ? error?.error?.message : (error?.errmsg ? error?.errmsg : translate('SERVICE.INTERNAL_SERVER_ERROR')));
          this.toastrService.errMessage(message, title);
        }, 500);
        });
  }

  getSolutionById(solutionId): void {
    this.solutionsService.getSolutionById(solutionId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(result => {
        this.solution = result;
        this.isLoading = false;

      },
        (error) => {
        setTimeout(() => {
		      const title = translate('ERRORS.SERVICE_AVAILABILITY');
          const message = error?.message ? error?.message : (error?.error?.message ? error?.error?.message : (error?.errmsg ? error?.errmsg : translate('SERVICE.INTERNAL_SERVER_ERROR')));
          this.toastrService.errMessage(message, title);
        }, 500);
        });
  }

   getRleaseVersions(solutionId, targetSolutionVersionId): void {
    if (targetSolutionVersionId) {
      this.solutionsService.getSolutionVersion(solutionId, targetSolutionVersionId)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(task => {
        },
          (error) => {
        setTimeout(() => {
		      const title = translate('ERRORS.SERVICE_AVAILABILITY');
          const message = error?.message ? error?.message : (error?.error?.message ? error?.error?.message : (error?.errmsg ? error?.errmsg : translate('SERVICE.INTERNAL_SERVER_ERROR')));
          this.toastrService.errMessage(message, title);
        }, 500);
          });
    }
  }


  /**
   * Goes to selected tab when tab selection changes
   * @param - the section name of selected tab
   */
  gotoSection(section: string): void {
    switch (section) {
      case this.InstanceTabs.OVERVIEW:
        this.isOverview = true;
        this.isEndpoints = false;
        this.isReleasePack = false;
        break;
      case this.InstanceTabs.VIEW_ENDPOINTS:
        this.isOverview = false;
        this.isEndpoints = true;
        this.isReleasePack = false;
        break;
      case this.InstanceTabs.RELEASE_PACKAGE:
        const instance = this.allInstances?.find(item => item.id === this.instance.id);
        this.updateInstance(instance, this.instance?.latestConfigurationState?.status);
        this.isOverview = false;
        this.isEndpoints = false;
        break;
      default:
        break;
    }
  }

  navigateToChangeTask(event): void {
    // this.router.navigate(['/', this.authService.principal.provider, RouterSegments.TENANT, this.tenantId, RouterSegments.CONTINUOUS_UPDATE,
    //   event?.taskInfo.solutionId, this.instanceId, this.changeTaskId, RouterSegments.INSTANCE
    // ]);
  }

  updateInstnace(event): void {
    if (event) {
      this.getInstanceDetails();
    }
  }

  private resolveLiveStatus(instances: Instance): boolean {
    if (instances.deploymentClass === DeploymentClass.A) {
      return this.INSTANCE_DEPLOYED_STATES.includes(instances?.status);
    }
  }

  // Displays copied icon after URL copied
  onCopyId(data): void {
    this.copy = true;
    this.clipboardApi.copyFromContent(data);
    setTimeout(() => {
      this.copy = false;
    }, 500);
  }

  /**
   * Opens URL in separate tab when URL is clicked
   * @param - the URL which is clicked
   */
  navigateToUrl(url): void {
    this.urlService.openUrlInNewTab(url);
  }

  // Reports issue when endpoints not available
  openContactForm(): void {
    this.isLoading = true;
    const data: any = {
      title: translate('INSTANCE_SUMMARY.REPORT_ISSUE.TITLE'),
      subtitle: '',
      report: {
        service: this.service,
        blueprint: this.blueprint,
        instance: this.instance
      },
      buildNumber: this.buildNumber
    };
    combineLatest([this.blueprintsService.getBlueprintsIdLatestVersion(this.blueprint.id),
    this.configService.getConfigurations(this.blueprint.id)])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([blueprint, configurations]) => {
        this.instance.activeConfiguration = configurations.find(item => item.id === this.instance.configurationId);
        data.report.instance = this.instance;
        data.version = blueprint.version;
        data.configurations = configurations;
        this.isLoading = false;
      }, (error) => {
        setTimeout(() => {
		  const title = translate('ERRORS.SERVICE_AVAILABILITY');
          const message = error?.message ? error?.message : (error?.error?.message ? error?.error?.message : (error?.errmsg ? error?.errmsg : translate('SERVICE.INTERNAL_SERVER_ERROR')));
          this.toastrService.errMessage(message, title);
        }, 500);
      });
  }

  openUrl(): void {
    const url = `${this.currentEnvironment}${this.data?.tenantId}/services/${this.data?.solutionId}/blueprints/${this.data?.blueprintId}`;
    window.open(url, '_blank');
  }

  openUrlSolution(id): void {
    const url = `${this.currentEnvironment}${this.data?.tenantId}/services/${id}`;
    window.open(url, '_blank');
  }

  initialLoad(): void {
    this.showSpinner = true;
    this.isFailed = false;
    this.routeData = [];
    this.loadsData(this.routeData);
    if (this.direction) {
      localStorage.setItem('filteredDirection', this.direction);
    } else {
      localStorage.removeItem('filteredDirection');
    }
    if (this.status) {
      localStorage.setItem('filteredSftpStatus', this.status);
    } else {
      localStorage.removeItem('filteredSftpStatus');
    }
    if (this.data.name === 'Demo Instance') {
      this.sftp = false;
    } else {
      this.sftp = true;
    }
    this.instanceService.getInstanceDetailsSftpDetailsById(this.data.id, this.direction ? this.direction : undefined, this.status ? this.status : undefined)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(data => {
        this.showSpinner = false;
        this.isFailed = true;
        this.routeData = data;
        this.loadsData(this.routeData);
      },
        (error) => {
        setTimeout(() => {
		      const title = translate('ERRORS.SERVICE_AVAILABILITY');
          const message = error?.message ? error?.message : (error?.error?.message ? error?.error?.message : (error?.errmsg ? error?.errmsg : translate('SERVICE.INTERNAL_SERVER_ERROR')));
          this.toastrService.errMessage(message, title);
        }, 500);
          this.showSpinner = false;
          this.isFailed = true;
        });
  }

  openRouteDetails(loggedResponse): any {
    this.instanceService.getInstanceDetailsSftpDetailsByIdByRouteID(this.data.id, loggedResponse.routeId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(loggedData => {
        this.sidenavService.openInRightSidenav(InstanceDetailsAddRouteComponent, { loggedData, edit: true });
      });
  }

  countSuccess(data): any {
    let count = 0;
    data?.components?.forEach(element => {
      if (element.status === translate('INSTANCE_STATUS.SUCCESS')) {
        count = count + 1;
      }
    });
    return count;
  }

  countFailure(data): any {
    let count = 0;
    data?.components?.forEach(element => {
      if (element.status === translate('INSTANCE_STATUS.FAILED')) {
        count = count + 1;
      }
    });
    return count;
  }

  addClassDuration(data, status): any {
    if (data.startsWith('00:')) {
      if (status === 'success') {
        this.addColor = 'green';
      } else {
        this.addColor = 'white';
      }
    } else {
      this.addColor = '';
    }
    return this.addColor;
  }

  filterColumns(): void {
    if (this.direction) {
      this.dropdownsList[0].options.filter((item) => {
        if (item.value === this.direction) {
          item.selected = true;
        }
      });
    } else {
      this.dropdownsList[0].options.filter((item) => {
        item.selected = false;
      });
    }
    if (this.status) {
      this.dropdownsList[1].options.filter((item) => {
        if (item.value === this.status) {
          item.selected = true;
        }
      });
    } else {
      this.dropdownsList[1].options.filter((item) => {
        item.selected = false;
      });
    }
    const data = {
      name: translate('INSTANCE_STATUS.ROUTE'),
      title: translate('INSTANCE_STATUS.FILTER_TITLE'),
      subtitle: translate('INSTANCE_STATUS.FILTER_SUBTITLE'),
      filteredColumns: this.dropdownsList,
      direction: this.dropdownsList[0].options,
      status: this.dropdownsList[1].options,
    };
    this.sidenavService.openInRightSidenav(ColumnFilterComponent, data)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(filteredColumns => {
        if (filteredColumns) {
          this.clearData = true;
          if (filteredColumns[0] !== '') {
            this.direction = filteredColumns[0];
          }
          if (filteredColumns[1] !== '') {
            this.status = filteredColumns[1];
          }
          this.initialLoad();
        }
      });
  }

  filteredColumnsFunction(data): void {
    if (this.direction === data) {
      this.direction = undefined;
      this.resetValue(this.dropdownsList[0]);
    } else if (this.status === data) {
      this.status = undefined;
      this.resetValue(this.dropdownsList[1]);
    }
    this.initialLoad();
  }

  resetFilter(): void {
    this.direction = undefined;
    this.status = undefined;
    this.clearData = false;
    this.sidenavService.searchFilter.next('data');
    this.resetAllValues();
    this.initialLoad();
  }

  resetValue(closedItem): void {
    closedItem?.options?.forEach(element => {
      element.selected = false;
    });
  }

  resetAllValues(): void {
    this.dropdownsList.forEach(element => {
      element?.options?.forEach(item => {
        item.selected = false;
      });
    });
  }

  /**
   * Goes to selected tab when tab selection changes in release package page
   * @param - the section name of selected tab
   */
  public tabChange(event): void {
    this.selectedTab = event;
    if (event === 1) {
      this.loadConfigurations();
    }
  }

  /**
   * Filter the configuration results according to search term and sort them
   */
  filterRows(): void {
    // Remove all configuration states
    if (this.filteredConfigurationStates) {
      this.filteredConfigurationStates.splice(0, this.filteredConfigurationStates.length);
    }

    if (this.configurationStates?.length > 0) {
      this.filteredConfigurationStates = [...this.configurationStates]
        .sort(this.propertyComparator('applicationFinishTime', SortDirection.DESC))
        .filter(configurationState => Constants.CONFIGURATION_FINAL_STATES.includes(configurationState.status));
    }


    if (this.searchTerm != null) {
      const searchBy = this.searchTerm.trim().toLocaleLowerCase();
      this.filteredConfigurationStates = this.filteredConfigurationStates.filter(configurationState =>
        configurationState.status?.toLocaleLowerCase().includes(searchBy)
        || this.configurationsMap[configurationState.configurationId].manifest.name.toLocaleLowerCase().includes(searchBy)
        || this.configurationsMap[configurationState.configurationId].manifest.version.toLocaleLowerCase().includes(searchBy));
    }
    this.paginator.length = this.filteredConfigurationStates.length;
  }

  /**
   * Opens the selected result in separate tab for viewing
   * @param - the selected configuration state and file name
   */
  viewTestResults(configurationState: ConfigurationState, filename: string): void {
    this.crvtService.getViewResultsUrl(configurationState.blueprintId, configurationState.instanceId, configurationState.id, filename, this.clientName)
    .pipe(takeUntilDestroyed(this.destroyRef))
    .subscribe((viewUrl) => {
      this.urlService.openUrlInNewTab(viewUrl);
    });
  }

  /**
   * Downloads the selected result
   * @param - the selected configuration state and file name
   */
  downloadTestResults(configurationState: ConfigurationState, filename: string): void {
    const configuration = this.configurationsMap[configurationState.configurationId];
    this.crvtService.downloadCRVT(configuration.blueprintId, configurationState.instanceId, configurationState.id, filename, this.clientName)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();
  }

  /**
   * Calls filter function when search term changes
   * @param - the search term entered
   */
  search(searchTerm?: string): void {
    this.searchTerm = searchTerm;
    this.filterRows();
  }

  /**
   * Sorts the configuration states
   * @param - the column property to sort
   */
  doSort(property: string): void {
    if (!this.sort || this.sort.property !== property) {
      this.sort = {
        property,
        direction: SortDirection.ASC
      };
    } else if (this.sort.direction === SortDirection.ASC) {
      this.sort.direction = SortDirection.DESC;
    } else {
      this.sort = null;
    }

    if (!this.sort) {
      this.filteredConfigurationStates = this.filteredConfigurationStates.sort(this.propertyComparator('applicationFinishTime',
        SortDirection.DESC));
    } else if (this.sort.property === 'duration') {
      this.filteredConfigurationStates = this.filteredConfigurationStates.sort(this.durationComparator(this.sort.direction));
    } else {
      this.filteredConfigurationStates = this.filteredConfigurationStates.sort(this.propertyComparator(this.sort.property,
        this.sort.direction));
    }
    this.applyPaginator();
  }


  /**
   * Calls get available configurations function when apply is clicked for a package
   * @param - the instance of selected cofiguration
   */
  updateInstance(instance: Instance, configStatus): void {
    this.availableConfigurations = this.getAvailableConfigurations(instance);
    this.isReleasePack = true;
  }

  /**
   * Filters configuration states when filter options changes
   * @param - the option selected in filter
   */
  filterOptionChange(option): void {
    this.selectedFilter = option;
    if (option.value) {
      this.configurationStates = this.allConfigurationStates.filter(item => option.value.includes(item?.status)) || [];
    } else {
      this.configurationStates = this.allConfigurationStates || [];
    }
    this.filterRows();
  }

  onTabChange($event: { index: number }) {
    setTimeout(() => {
      if (document.getElementsByClassName("cdk-overlay-container")) {
        const element = <HTMLElement>document.getElementsByClassName("cdk-overlay-container")[0];
        element.style.display = 'block';
      }
    }, 100);
    this.activeTab = 0;
    if ($event.index === 7) {
      this.sftpPaginator = true;
      this.logPaginator = false;
    } else if ($event.index === 10) {
      this.onLogTabChange($event);
      this.sftpPaginator = false;
      this.logPaginator = true;
    } else {
      this.logPaginator = false;
      this.sftpPaginator = false;
    }
  }

  onLogTabChange($event?: { index: number }): any {
    if ($event.index === 0 || $event.index === 10) {
      this.deploymentLogData = this.logData;
      if (this.selectedActions?.length > 0) {
        this.deploymentLogData = this.logData?.filter(item => this.selectedActions?.includes(item?.action));
      } else {
        this.deploymentLogData = this.logData;
      }
      if (this.filterStartDate && this.filterEndDate) {
        this.deploymentLogData = this.deploymentLogData.filter(item => this.datePipe.transform(item.startTime, 'yyyy-MM-dd') >= this.datePipe.transform(this.filterStartDate, 'yyyy-MM-dd') &&
          this.datePipe.transform(item.finishTime, 'yyyy-MM-dd') <= this.datePipe.transform(this.filterEndDate, 'yyyy-MM-dd'));
      }

      this.dataSourceLog = this.deploymentLogData;
      this.deploymentLog = true;
      if (this.sortInfo) {
        const sortedData = this.sortDeployData(this.sortInfo);
        this.dataSourceLog = new MatTableDataSource(sortedData?.data?.slice(this.pageIndex, this.paginationSize));
      } else {
        const sortedData = this.sortDeployData({ active: 'startTime', direction: 'desc' });
        this.dataSourceLog = new MatTableDataSource(sortedData?.data?.slice(this.pageIndex, this.paginationSize));
      }
    } else {
      this.updatedLogData = this.logData?.filter(data => data.action === translate('INSTANCE_STATUS.UPDATE'));
      this.dataSourceUpdateLog = this.updatedLogData;
      this.deploymentLog = false;
      this.updatedLog = true;
      if (this.sortInfo) {
        const sortedData = this.sortDeployData(this.sortInfo);
        this.dataSourceUpdateLog = new MatTableDataSource(sortedData?.data?.slice(this.pageIndex, this.paginationSize));
      } else {
        const sortedData = this.sortDeployData({ active: 'startTime', direction: 'desc' });
        this.dataSourceUpdateLog = new MatTableDataSource(sortedData?.data?.slice(this.pageIndex, this.paginationSize));
      }
    }
  }

  createRoutes(): any {
    const disabledStatus = [DeployStatus.TERMINATION_FAILED, DeployStatus.DEPLOYING,
    DeployStatus.FAILED, DeployStatus.TERMINATED, DeployStatus.TERMINATING];
    if (disabledStatus.find(item => item === this.data.status) || !this.sftp) {
      this.dialog.open(GenericErrorComponent, {
        minHeight: '342px',
        width: '566px',
        panelClass: 'flex-dialog'
      });
    } else {
      const data = {
        id: this.data.id
      };
      this.sidenavService.openInRightSidenav(InstanceDetailsAddRouteComponent, data)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((formData) => {
          if (!formData) {
            return;
          } else {
            this.routeData = formData;
            setTimeout(() => {
              const title = translate('TENANT_DETAILS.SFTP');
              const message = translate('INSTANCE_DETAILS.CREATE_ROUTE');
              this.toastrService.successMessage(message, title);
            }, 500);
           this.initialLoad();
          }
        });
    }
  }

  sortDeployData(event, filteredList?): any {
    let data;
    if (this.deploymentLog) {
      data = this.deploymentLogData;
    } else if (this.updatedLog) {
      data = this.updatedLogData;
    }
    if (filteredList) {
      data = filteredList;
    }
    return data?.sort((a, b) => {
      let comparatorResult = 0;
      switch (event.active) {
        case 'startTime':
          comparatorResult = a?.startTime && b?.startTime ? a?.startTime?.localeCompare(b?.startTime) : !a?.startTime ? -1 : 1;
          break;
        case 'finishTime':
          comparatorResult = a?.finishTime && b?.finishTime ? a?.finishTime?.localeCompare(b?.finishTime) : !a?.finishTime ? -1 : 1;
          break;
      }
      return comparatorResult * (event.direction === 'asc' ? 1 : -1);
    });
  }

  dateFilter() {
    if (this.selectedActions) {
      this.activityDropdownsList[0]?.options?.map((item) => item.selected = false);
      this.selectedActions.forEach(item => {
        this.activityDropdownsList[0].options.find(option => option.value?.toLowerCase() === item?.toLowerCase()).selected = true;
      })
    } else {
      this.activityDropdownsList[0].options.filter((item) => {
        item.selected = false;
      });
    }
    const data = {
      name: 'InstanceActivity',
      title: translate('INSTANCE_STATUS.FILTER_TITLE'),
      subtitle: translate('INSTANCE_STATUS.FILTER_SUBTITLE'),
      filteredColumns: this.activityDropdownsList,
      action: this.activityDropdownsList[0].options,
      startDate: this.filterStartDate,
      endDate: this.filterEndDate
    };
    this.sidenavService.openInRightSidenav(ColumnFilterComponent, data)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(filteredColumns => {
        if (filteredColumns[0].length === 0 && !filteredColumns[1]?.filteredStartDate && !filteredColumns[1]?.filterEndDate) {
          this.selectedActions = [];
          this.activityDropdownsList[0]?.options?.map((item) => item.selected = false);
          this.filterStartDate = undefined;
          this.filterEndDate = undefined;
          this.deploymentLogData = this.logData;
        }
        if (filteredColumns) {
          if (filteredColumns[0]?.length > 0 || filteredColumns[0] !== this.previousSelectedActions) {
            this.previousSelectedActions = filteredColumns[0];
            this.selectedActions = filteredColumns[0];
            if (filteredColumns[0].length > 0) {
              this.deploymentLogData = this.logData?.filter(item => this.selectedActions?.includes(item?.action));
            } else {
              this.deploymentLogData = this.logData;
            }
          }
          if (filteredColumns[1] && filteredColumns[1].filteredStartDate && filteredColumns[1].filteredEndDate) {
            this.filterStartDate = filteredColumns[1].filteredStartDate;
            this.filterEndDate = filteredColumns[1].filteredEndDate;
            this.deploymentLogData = this.deploymentLogData.filter(item => this.datePipe.transform(item.startTime, 'yyyy-MM-dd') >= this.datePipe.transform(this.filterStartDate, 'yyyy-MM-dd') &&
              this.datePipe.transform(item.finishTime, 'yyyy-MM-dd') <= this.datePipe.transform(this.filterEndDate, 'yyyy-MM-dd'));
          }
        }
        this.sortData(this.sortInfo ? this.sortInfo : { active: 'startTime', direction: 'desc' });
      })
  }

  cancelFilter() {
    this.filterStartDate = undefined;
    this.filterEndDate = undefined;
    if (this.selectedActions?.length > 0) {
      this.deploymentLogData = this.logData?.filter(item => this.selectedActions?.includes(item?.action));
    } else {
      this.deploymentLogData = this.logData;
    }
    this.sortData(this.sortInfo ? this.sortInfo : { active: 'startTime', direction: 'desc' });
  }

  cancelActionFilter() {
    this.selectedActions = [];
    this.activityDropdownsList[0]?.options?.map((item) => item.selected = false);
    if (this.filterStartDate && this.filterEndDate) {
      this.deploymentLogData = this.logData.filter(item => this.datePipe.transform(item.startTime, 'yyyy-MM-dd') >= this.datePipe.transform(this.filterStartDate, 'yyyy-MM-dd') &&
        this.datePipe.transform(item.finishTime, 'yyyy-MM-dd') <= this.datePipe.transform(this.filterEndDate, 'yyyy-MM-dd'));
    } else {
      this.deploymentLogData = this.logData;
    }
    this.sortData(this.sortInfo ? this.sortInfo : { active: 'startTime', direction: 'desc' });
  }

  sortData(event): void {
    this.showPagination = false;
    setTimeout(() => {
      this.showPagination = true;
    }, 10);
    this.pageIndex = 0;
    if (this.deploymentLog) {
      this.sortInfo = event;
      if (this.filteredDeploymentLog?.length >= 0) {
        this.filteredDeploymentLog = this.sortDeployData(event, this.filteredDeploymentLog);
      } else {
        this.deploymentLogData = this.sortDeployData(event);
      }
      this.expandedElement = null;
      const data = this.filteredDeploymentLog ? this.filteredDeploymentLog : this.deploymentLogData;
      this.dataSourceLog = new MatTableDataSource(data?.slice(this.pageIndex, this.paginationSize));
    } else if (this.updatedLog) {
      this.sortInfo = event;
      if (this.filteredUpdateLog) {
        this.filteredUpdateLog = this.sortDeployData(event, this.filteredUpdateLog);
      } else {
        this.updatedLogData = this.sortDeployData(event);
      }
      this.expandedElement = null;
      const data = this.filteredUpdateLog ? this.filteredUpdateLog : this.updatedLogData;
      this.dataSourceUpdateLog = new MatTableDataSource(data?.slice(this.pageIndex, this.paginationSize));
    } else {
      this.routeData = this.sortInstanceData(event);
      this.dataSource = new MatTableDataSource(this.routeData?.slice(this.pageIndex, this.paginationSize));
    }
  }

  logPageChanged(event): any {
    if (event.length) {
      this.expandedElement = null;
      this.logData.forEach(item => {
        item.arrowClick = false;
      });
      this.pageIndex = event.pageIndex;
      this.paginationSize = event.pageSize;
      const firstIndex = this.pageIndex * this.paginationSize;
      if (this.deploymentLog) {
        const data = this.filteredDeploymentLog ? this.filteredDeploymentLog : this.deploymentLogData;
        const dataList = data?.slice(firstIndex, firstIndex + this.paginationSize);
        this.dataSourceLog = new MatTableDataSource(dataList);
      } else {
        const data = this.filteredUpdateLog ? this.filteredUpdateLog : this.updatedLogData;
        const dataList = data?.slice(firstIndex, firstIndex + this.paginationSize);
        this.dataSourceUpdateLog = new MatTableDataSource(dataList);
      }

    }
  }

  onArrowClick(data): void {
    this.expandedElement = this.expandedElement === data ? null : data;
    this.dataSourceLog.filteredData.find(item => item.contextId === data.contextId).arrowClick = !this.dataSourceLog.filteredData.find(item => item.contextId === data.contextId).arrowClick;
    this.dataSourceLog.filteredData.forEach(item => {
      if (item.contextId !== data.contextId) {
        item.arrowClick = false;
      }
    });
    this.dataSourceInnerLog = this.dataSourceLog.filteredData.find(item => item.contextId === data.contextId).components;
    setTimeout(() => {
      if (document.getElementsByClassName("cdk-overlay-container")) {
        const element = <HTMLElement>document.getElementsByClassName("cdk-overlay-container")[0];
        element.style.display = 'none';
      }
    }, 200);
  }

  getBuildVersion(solutionVersionId: any): string {
    const solnVersion = this.serviceVersions?.find(v => v.id === solutionVersionId);
    this.solutionVersion = solnVersion;
    return solnVersion?.releaseVersion;
  }

  // Gets solution details based on service id and adds in breadcrumbs
  private loadService(): void {
    const blueprintId = this.activatedRoute.snapshot.params.blueprintId;
    this.blueprintsService.getBlueprints(this.data?.tenantId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((blueprints: any) => {
        this.getBlueprints = blueprints?.find(item => item.id == blueprintId);
        const solutionId = this.getBlueprints?.solutionId;
        this.tenantId = this.getBlueprints?.tenantId;
        if (this.fromCU) {
          // Show Continuous Update breadcrumbs
          this.breadcrumbItems = [
            { icon: 'assets/images/home_icon.svg', url: [RouterSegments.TENANT, this.tenantId] },
            { label: translate('TENANT_CONTINUOUS_UPDATE.CONTINUOUS_UPDATE'), url: [RouterSegments.TENANT, this.tenantId, RouterSegments.CONTINUOUS_UPDATE] },
            { label: this.getBlueprints?.name, url: [RouterSegments.TENANT, this.tenantId, RouterSegments.CONTINUOUS_UPDATE, 
               this.solutionId, this.blueprintId, RouterSegments.INSTANCE] },  
          ];
        } else {
          // Default Service Definition breadcrumbs
          this.breadcrumbItems = [
            { icon: 'assets/images/home_icon.svg', url: [RouterSegments.TENANT, this.tenantId] },
            { label: translate('TENANT_LAYOUT.MENU.SERVICE_DEFINITION'), url: [RouterSegments.TENANT, this.tenantId, RouterSegments.SERVICE_DEFINITION] },
            { label: this.getBlueprints?.name, url: [RouterSegments.TENANT, this.tenantId, RouterSegments.BLUEPRINTS, this.getBlueprints?.id] }
          ];
        }
        if (solutionId) {
          combineLatest([this.solutionsService.getSolutionDetailsById(solutionId), this.solutionsService.getSolutionVersions(solutionId)])
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(([service, serviceVersions]) => {
              this.service = service;
              this.serviceVersions = serviceVersions;
              if (this.solutionVersions) {
                this.viewReleaseNotes = this.solutionVersions[this.solutionVersions?.length - 1]?.releaseNotes;
              }
              this.getInstanceDetails();
            }, (error) => {
        setTimeout(() => {
		  const title = translate('ERRORS.SERVICE_AVAILABILITY');
          const message = error?.message ? error?.message : (error?.error?.message ? error?.error?.message : (error?.errmsg ? error?.errmsg : translate('SERVICE.INTERNAL_SERVER_ERROR')));
          this.toastrService.errMessage(message, title);
        }, 500);
            });
        }
      });
  }

  // Gets blueprint details based on blueprint id and sets environment for instance
  private loadBlueprint(): void {
    //Enable this whne the get_mgmt_blueprints_id is available
    // const blueprintId = this.activatedRoute.snapshot.params.blueprintId;
    // this.blueprintsService.getBlueprint(blueprintId)
    //   .pipe(takeUntilDestroyed(this.destroyRef))
    //   .subscribe(blueprint => {
    //     this.blueprint = blueprint;
    //     if (this.instance.deploymentClass === InstanceClass.D) {
    //       this.instance.environment = 'DEV';
    //     } else {
    //       this.instance.environment = this.blueprint?.pathToLiveConfiguration
    //         .find(item => item?.instanceId === this.instance?.id)?.environment;
    //     }
    //     this.breadcrumbItems.push({ label: this.blueprint.name, url: ['services', this.service.id, 'blueprints', this.blueprint.id] });
    //     this.breadcrumbItems.push({ label: this.instance.name });
    //     this.istanceLoaded = true;
    //     this.loadData();
    //   }, (err) => this.errorPageService.show404Page());

    this.blueprint = this.getBlueprints;
    if (this.instance.deploymentClass === DeploymentClass.D) {
      this.instance.environment = 'DEV';
    } else {
      this.instance.environment = this.blueprint?.pathToLiveConfiguration
        .find(item => item?.instanceId === this.instance?.id)?.environment;
    }
    this.breadcrumbItems.push({ label: this.blueprint.name, url: ['services', this.service.id, 'blueprints', this.blueprint.id] });
    this.breadcrumbItems.push({ label: this.instance.name });
    this.istanceLoaded = true;
    this.loadData();
  }

  // Loads configuration states list according to selected filter
  private loadConfigurations(): void {
    if (this.selectedFilter.value) {
      this.configurationStates = this.allConfigurationStates.filter(item => this.selectedFilter.value.includes(item?.status)) || [];
    } else {
      this.configurationStates = this.allConfigurationStates || [];
    }
    this.configurationsMap = this.configurations?.reduce((acc, configuration) => {
      acc[configuration.id] = configuration;
      return acc;
    }, {});
    this.filterRows();
  }

  // Shows configuration states according to page selection
  private applyPaginator(): void {
    const startIndex = this.paginator.pageIndex * this.paginator.pageSize;
    this.pageConfigurationStates = this.filteredConfigurationStates?.slice(startIndex, startIndex + this.paginator.pageSize);
    this.changeDetectorRef.detectChanges();
  }

  /**
   * Sorts configuration states
   * @param - the property and direction to sort
   */
  private propertyComparator(property: string, direction: SortDirection): ComparatorFunc {
    return (configurationState1: ConfigurationState, configurationState2: ConfigurationState): number => {
      if (isDate(configurationState1[property])) {
        return direction === SortDirection.ASC
          ? configurationState1[property]?.getTime() - configurationState2[property]?.getTime()
          : configurationState2[property]?.getTime() - configurationState1[property]?.getTime();
      } else {
        if (configurationState1[property] && configurationState2[property]) {
          return direction === SortDirection.ASC
            ? configurationState1[property].localeCompare(configurationState2[property])
            : -(configurationState1[property].localeCompare(configurationState1[property]));
        }
      }
    };
  }

  /**
   * Sorts configuration states for time related column
   * @param - the direction to sort
   */
  private durationComparator(direction: SortDirection): ComparatorFunc {
    return (configurationState1: ConfigurationState, configurationState2: ConfigurationState): number => {
      if (configurationState1.applicationStartTime && configurationState1.applicationFinishTime
        && configurationState2.applicationStartTime && configurationState2.applicationFinishTime) {
        const duration1 = new Date(configurationState1.applicationFinishTime).getTime()
          - new Date(configurationState1.applicationStartTime).getTime();

        const duration2 = new Date(configurationState2.applicationFinishTime).getTime()
          - new Date(configurationState2.applicationStartTime).getTime();

        return direction === SortDirection.ASC ? duration1 - duration2 : duration2 - duration1;
      }
      return 0;
    };
  }

  /**
   * Populates available configurations according to selected instance
   * @param - the instance selected
   */
  private getAvailableConfigurations(instance: Instance): Configuration[] {
    const requiredEnvironment = this.blueprint?.pathToLiveConfiguration.find(
      ptl => ptl.environment === instance?.environment && ptl.instanceId === instance.id)?.requires;
    let availableConfigurations = this.configurations;
    // Only configurations which are created before (or equal) latest successfully applised configuration on previous environment
    const prodInstance = this.allInstances?.find(instances => instances.id === this.instance.id);
    if (this.instance.dataZone === DataZone.LIVE &&
      prodInstance?.configurationStates?.find(config => config.status === ConfigurationApplicationStatus.VERIFIED)) {
      const latestVerifiedInstance = prodInstance.configurationStates
        .find(configurationState => configurationState.status === ConfigurationApplicationStatus.VERIFIED);
      const verifiedInstanceConfig =
        this.getConfigurationById(latestVerifiedInstance.configurationId);
      availableConfigurations = availableConfigurations
        .filter(configuration => configuration.dateCreated === verifiedInstanceConfig.dateCreated);
    } else if (requiredEnvironment) {
      const requiredInstance = this.allInstances.find(ptlInstance => ptlInstance.environment === requiredEnvironment);
      if (requiredInstance?.configurationStates) {
        const latestSuccessfulStateOfRequiredInstance = requiredInstance.configurationStates
          .find(configurationState => configurationState.status === ConfigurationApplicationStatus.SUCCESS);
        if (latestSuccessfulStateOfRequiredInstance) {
          const configAvailable = availableConfigurations;
          const latestAppliedConfigurationOnRequiredInstance =
            this.getConfigurationById(latestSuccessfulStateOfRequiredInstance.configurationId);
          availableConfigurations = [];
          for (const configStates of requiredInstance.configurationStates) {
            if (configStates.status === ConfigurationApplicationStatus.SUCCESS) {
              for (const availableConfig of configAvailable) {
                if (availableConfig.id === configStates.configurationId) {
                  availableConfigurations.push(availableConfig);
                }
                availableConfigurations = [...new Set(availableConfigurations)];
              }
            }
          }
        } else {
          availableConfigurations = [];
        }
      } else {
        availableConfigurations = [];
      }
    }
    if (instance?.configurationStates) {
      const latestSuccessfulState = instance.configurationStates
        .find(configurationState => configurationState.status === ConfigurationApplicationStatus.SUCCESS);
      if (latestSuccessfulState) {
        const latestAppliedConfiguration = this.getConfigurationById(latestSuccessfulState.configurationId);
        availableConfigurations = availableConfigurations
          .filter(configuration => configuration.dateCreated > latestAppliedConfiguration.dateCreated);
      }
    }

    return availableConfigurations;
  }

  /**
   * Returns configuration corresponding to given configuration id
   * @param - the configuration id
   */
  private getConfigurationById(configurationId: string): Configuration {
    return this.configurations.find(configuration => configuration.id === configurationId);
  }

  loadsData(data): void {
    this.pageIndex = 0;
    if (this.sortInfo) {
      const sortedData = this.sortInstanceData(this.sortInfo);
      this.dataSource = new MatTableDataSource(sortedData?.data?.slice(this.pageIndex, this.paginationSize));
    } else {
      const sortedData = this.sortInstanceData({ active: 'status', direction: 'asc' });
      this.dataSource = new MatTableDataSource(sortedData?.data?.slice(this.pageIndex, this.paginationSize));
    }
    this.addingStatus = true;
    if (data) {
      data.forEach(element => {
        if (element?.status === 'adding') {
          this.addingStatus = false;
        }
      });
    }
  }

  sortInstanceData(event): any {
    return this.routeData?.sort((a, b) => {
      let comparatorResult = 0;
      switch (event.active) {
        case 'routeId':
          comparatorResult = a?.routeId && b?.routeId ? a?.routeId?.localeCompare(b?.routeId) : !a?.routeId ? -1 : 1;
          break;
        case 'routeName':
          comparatorResult = a?.routeName && b?.routeName ? a?.routeName?.localeCompare(b?.routeName) : !a?.routeName ? -1 : 1;
          break;
        case 'direction':
          comparatorResult = a?.direction && b?.direction ? a?.direction?.localeCompare(b?.direction) : !a?.direction ? -1 : 1;
          break;
        case 'status':
          comparatorResult = a?.status && b?.status ? a?.status?.localeCompare(b?.status)
            : !a?.status ? -1 : 1;
          break;
        default:
          comparatorResult = a?.status && b?.status ? a?.status?.localeCompare(b?.status) : !a?.status ? -1 : 1;
          break;
      }
      return comparatorResult * (event.direction === 'asc' ? 1 : -1);
    });
  }



  displayAgentStatus(agentStatus?: any): string {
    if (agentStatus === 'running' || agentStatus === 'stop_failed') {
      return 'Stop Agent';
    } else {
      return 'Start Agent';
    }
  }

  /**
 * Changing agent status corresponding to given configuration id
 * @param - the configuration id
 */

  refreshInstances(): void {
    this.isLoading = true;
    this.count = this.count !== 1 ? 1 : 0;
    this.getInstanceDetails();
  }

  changeAgentStatus(data?: Instance): void {
    if ((this.data?.dataZone === DataZone.DEVELOPMENT && !this.permissionsMap[PermissionKeys.INSTANCE_AGENT_MANAGE_DATAZONE_NONPII]) || (this.data?.dataZone === DataZone.SECURE && !this.permissionsMap[PermissionKeys.INSTANCE_AGENT_MANAGE_DATAZONE_PII]) ||
      (this.data?.dataZone === DataZone.LIVE && !this.permissionsMap[PermissionKeys.INSTANCE_AGENT_MANAGE_DATAZONE_LIVE]) || this.INSTANCE_DEPLOYING_STATES.includes(this.data?.status)) {
      return;
    }
    if (data.agentStatus === this.AgentStatus.RUNNING || data.agentStatus === this.AgentStatus.STOP_FAILED)
      this.dialog.open(ConfirmationDialogComponent, {
        panelClass: 'default-dialog-wrapper',
        width: '566px',
        autoFocus: false,
        data: {
          title: translate('INSTANCE_DETAILS.AGENT_STOP_TITLE'),
          message: translate('INSTANCE_DETAILS.AGENT_STOP_DESC'),
          confirmButtonText: translate('BUTTONS.SUBMIT')
        }
      })
        .afterClosed()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(confirm => {
          if (confirm) {
      this.instanceService.agentStop(data)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          complete: () => {
            this.getInstanceDetails();
          },
          error: (error) => {
           const errorMessage = typeof error === 'string' ? error: typeof error?.error === 'string' ? error.error : error;
                this.dialog.open(ErrorDialogComponent, {
                  minHeight: '342px',
                  width: '566px',
                  panelClass: 'flex-dialog',
                  data: {
                    title: translate('ERROR_DIALOG.AGENT_STOP_FAILED'),
                    description: errorMessage,
                  }
               });
          }
        });
      }
    });

    if (data.agentStatus === this.AgentStatus.STOPPED || data.agentStatus === this.AgentStatus.START_FAILED)
      this.dialog.open(ConfirmationDialogComponent, {
        panelClass: 'default-dialog-wrapper',
        width: '566px',
        autoFocus: false,
        data: {
          title: translate('INSTANCE_DETAILS.AGENT_START_TITLE'),
          message: translate('INSTANCE_DETAILS.AGENT_START_DESC'),
          confirmButtonText: translate('BUTTONS.SUBMIT')
        }
      })
        .afterClosed()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(confirm => {
          if (confirm) {
      this.instanceService.agentStart(data)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          complete: () => {
            this.getInstanceDetails();
          },
          error: (error) => {
            const errorMessage = typeof error === 'string' ? error: typeof error?.error === 'string' ? error.error : error;
                this.dialog.open(ErrorDialogComponent, {
                  minHeight: '342px',
                  width: '566px',
                  panelClass: 'flex-dialog',
                  data: {
                    title: translate('ERROR_DIALOG.AGENT_START_FAILED'),
                    description: errorMessage,
                  }
               });
          }
        });
      }
    });
  }


  // Loads all the configurations and configuration states
  private loadData(): void {
    const blueprintId = this.activatedRoute.snapshot.params.blueprintId;
    if (this.loadDataSubscription) {
      this.loadDataSubscription.unsubscribe();
      this.loadDataSubscription = null;
    }

    this.loadDataSubscription = combineLatest([
      this.loadInstancesWithConfigurationState(blueprintId),
      //this.configService.getConfigurations(blueprintId)
    ])
      .pipe(
        takeUntilDestroyed(this.destroyRef))
      .subscribe(([instances]) => {
        const instanceId = this.activatedRoute.snapshot.params.instanceId;
        this.allInstances = instances;
        this.allInstances.forEach(instance => {
          if (instance.deploymentClass === DeploymentClass.D) {
            instance.environment = 'DEV';
          } else {
            instance.environment = this.blueprint?.pathToLiveConfiguration.find(item => item?.instanceId === instance?.id)?.environment;
          }
        });
        this.instance = this.allInstances.find(instance => instance.id === instanceId);
        // this.configurations = configurations;
        const availableConfigurations: any = this.getAvailableConfigurations(this.instance);
        availableConfigurations.forEach(config => {
          config.status = this.instance?.configurationStates?.find(item => item.configurationId === config.id)?.status;
          config.skipTest = this.instance?.configurationStates?.find(item => item.configurationId === config.id)?.skipTest;
        });
        this.availableConfigurations = availableConfigurations;
        this.configurationLoaded = true;
      }, (error) => {
        setTimeout(() => {
		      const title = translate('ERRORS.SERVICE_AVAILABILITY');
          const message = error?.message ? error?.message : (error?.error?.message ? error?.error?.message : (error?.errmsg ? error?.errmsg : translate('SERVICE.INTERNAL_SERVER_ERROR')));
          this.toastrService.errMessage(message, title);
        }, 500);
      });
  }

  /**
   * Loads configuration states for all instances
   * @param - the blueprint id
   */
  private loadInstancesWithConfigurationState(blueprintId: string): Observable<Instance[]> {
    return this.instanceService.getInstancesForBlueprint(blueprintId, this.tenantId)
      .pipe(mergeMap((instances) => {
        const instanceId = this.activatedRoute.snapshot.params.instanceId;
        this.instanceBlueprint = instances;
        const latestConfigRequests = this.instanceBlueprint
          .filter(instance => instance.configurationId);
        this.deployedDate = instances.length > 0 ? instances[instances.length - 1].dateUpdated : this.currentDate;
        // if (latestConfigRequests.length > 0) {
        //   return combineLatest(latestConfigRequests).pipe(map(configurationStates => {
        //     if (configurationStates) {
        //       configurationStates.forEach(configurationStatesForInstance => {
        //         if (configurationStatesForInstance?.length > 0) {
        //           const instance = instances.find(inst => configurationStatesForInstance[0].instanceId === inst.id);
        //           this.setInstanceConfigurationStates(instance, configurationStatesForInstance);
        //         }
        //       });
        //     }
        //     return instances;
        //   }));
        // }
        return of(instances);
      }
      ));
  }

  getInstanceActivityProgress(instanceId): void {
    this.activityService.getInstanceActivityLogs(instanceId)
    .pipe(takeUntilDestroyed(this.destroyRef))
    .subscribe(data => {
      if (data?.length === 0) {
        this.agentCreationProgress = DeployStatus.TENANT_AGENT_CREATION_IN_PROGRESS;
      }
      const activityLogs = data.sort((a, b) => new Date(b?.startTime)?.getTime() - new Date(a?.startTime)?.getTime() ? -1 : 1);
      if (activityLogs[0]?.count && activityLogs[0]?.count?.total) {
        this.countData.total = Number(activityLogs[0]?.count?.total);
        this.countData.completed = Number(activityLogs[0]?.count?.completed ? activityLogs[0]?.count?.completed : 0);
        this.countData.skipped = Number(activityLogs[0]?.count?.skipped ? activityLogs[0]?.count?.skipped : 0);
        this.countData.pending = Number(this.countData.total - (this.countData.completed + this.countData.skipped));
        this.countData.completedPercent = this.countData?.completed !== 0 ? Math.round(((this.countData?.completed + this.countData.skipped) / this.countData.total) * 100) : 0;
      }
    }, (error) => {
      clearInterval(this.intervalElement);
    })
  }

  /**
   * Sets configuration states for all instances
   * @param - instance and its configuration states
   */
  private setInstanceConfigurationStates(instance: Instance, configurationStates: ConfigurationState[]): void {
    if (instance?.id === this.instance?.id) {
      this.allConfigurationStates = configurationStates;
    }
    if (configurationStates.find(config => config.status === ConfigurationApplicationStatus.CONFIGURING ||
      config.status === ConfigurationApplicationStatus.VERIFYING)) {
      this.appliedConfigurationState = configurationStates.find(config => config.status === ConfigurationApplicationStatus.CONFIGURING);
    }
    if (configurationStates.find(config => config.id === this.appliedConfigurationState?.id &&
      config.status === ConfigurationApplicationStatus.SUCCESS)) {
      this.selectedTab = 1;
      this.loadConfigurations();
    }
    if (!instance || !configurationStates) {
      return;
    }
    configurationStates.sort((state1, state2) => state2.applicationStartTime.getTime() - state1.applicationStartTime.getTime());
    instance.configurationStates = configurationStates;
    if (instance.configurationStates?.length > 0) {
      instance.latestConfigurationState = instance.configurationStates[0];
    }
  }

  getReleasePackage(): void {
    this.configService.getConfigurationStates(this.instanceId)
    .pipe(takeUntilDestroyed(this.destroyRef))
    .subscribe(configurationStates => {
      const sortedConfigurationStates = configurationStates.sort((state1, state2) => state2?.applicationStartTime.getTime() - state1?.applicationStartTime.getTime());
      const latestSuccessfulState = sortedConfigurationStates
      .find(configurationState => configurationState.status === ConfigurationApplicationStatus.SUCCESS);
      this.releasePackage = this.configurations?.find(item => item.id === latestSuccessfulState?.configurationId)?.manifest?.name;
    })
  }

  // Subscribes for notifications for updating the configuration states status accordingly
  private subscribeForNotifications(): void {
    this.notificationsService.onAnyNotification([
      { type: 'com.temenos.saas.engine.configuration' },
      { subject: 'Change Task', data: { status: 'published' } },
      { subject: 'Change Task', data: { status: 'rejected' } },
      { subject: 'Change Task', data: { status: 'applying' } },
      { subject: 'Change Task', data: { status: 'applied' } }
    ])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((notification) => {
        if (notification.data?.blueprintId === this.blueprint?.id) {
          this.loadData();
        }
      });

    this.notificationsService.onAnyNotification([
      { type: 'com.temenos.saas.engine.configuration.apply' },
      { type: 'com.temenos.saas.engine.configuration.verify' }
    ])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((notification) => {
        const updatedInsance = this.allInstances?.find(instance => instance.id === notification.data?.instanceId);
        if (updatedInsance) {
          this.loadData();
        }
      });
  }

  viewErrors(instanceId): void {
    if (this.instanceDetailsError?.errors?.length > 0) {
      let dialogData = {
        title: translate('NOTIFICATIONS.DEPLOYMENT_FAILED'),
        descriptionError: this.instanceDetailsError?.errors,
        subtitle: instanceId?.name
      };
      if (this.data?.status === DeployStatus.TERMINATION_FAILED) {
        dialogData.title = translate('BLUEPRINT_SUMMARY.TERMINATION_FAILED');
      } else if (this.data?.status === DeployStatus.CONFIGURATION_FAILED) {
        dialogData.title = translate('BLUEPRINT_SUMMARY.CONFIGURATION_FAILED');
      } else if (this.data?.status === DeployStatus.TEST_FAILED) {
        dialogData.title = translate('CONFIGURATIONS.STATE.TEST_FAILED');
      } else if (this.data?.status === DeployStatus.FAILED) {
        dialogData.title = translate('BLUEPRINT_SUMMARY.FAILED');
      } else {
        dialogData.title = translate('NOTIFICATIONS.DEPLOYMENT_FAILED');
      }
      dialogData.descriptionError = this.instanceDetailsError?.errors;
      this.dialog.open(ErrorDialogComponent, {
        minHeight: '342px',
        width: '630px',
        panelClass: 'flex-dialog',
        data: dialogData
      });
    }
  } 

  /* onSwitchToNew(): void {
    this.switchToNewViewForAzure = true;
    this.router.navigate(['/',this.authService.principal.provider, RouterSegments.TENANT, this.tenantId, RouterSegments.BLUEPRINTS, this.blueprintId, 'environment', this.instanceId]);
    localStorage.setItem('switchToNewViewForAzure', JSON.stringify(this.switchToNewViewForAzure));
  } */
}
